import UPNG from '@upng/upng-js';
import imageCompression from 'browser-image-compression';
import { deflate } from 'pako/lib/deflate.js';
import { ensureFileScopeAuthorized } from '@/services/fileScope';
import { getCompanyInfo } from '@/utils/getCompanyInfo';

const JPEG_IMAGE_TYPES = ['image/jpeg', 'image/jpg'];
const PNG_IMAGE_TYPE = 'image/png';
const GIF_IMAGE_TYPE = 'image/gif';
const WEBP_IMAGE_TYPE = 'image/webp';
const COMPRESSIBLE_IMAGE_TYPES = [...JPEG_IMAGE_TYPES, PNG_IMAGE_TYPE, WEBP_IMAGE_TYPE];
const ALLOWED_IMAGE_TYPES = [...COMPRESSIBLE_IMAGE_TYPES, GIF_IMAGE_TYPE];

// 需求参数：图片最大边限制为 1920px，避免超大尺寸图片直接进入上传链路。
const MAX_IMAGE_WIDTH = 1920;
// 需求参数：压缩后的弹窗图片上传大小上限为 500KB。
const MAX_UPLOAD_SIZE = 500 * 1024;

// 需求参数（SKA 臻星恒通，站点 3363/3378）：白名单站点弹窗图片不做压缩，原图直传（仍受 500KB 上限约束）。
const NO_COMPRESS_COMPANY_IDS: number[] = [3363, 3378];
// 需求参数：扁平 PNG 使用调色板压缩时保留的最大颜色数。
const PNG_MAX_COLORS = 192;
// 需求参数：PNG 类型判断的采样尺寸，避免整图统计造成前端卡顿。
const PNG_CLASSIFICATION_SIZE = 200;
// 需求参数：PNG 采样区不同颜色数达到该阈值时，按照片类 PNG 处理。
const PNG_PHOTO_UNIQUE_COLOR_THRESHOLD = 2000;
const PNG_SIGNATURE = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]);
const PNG_IHDR_CHUNK_TYPE = new Uint8Array([0x49, 0x48, 0x44, 0x52]);
const PNG_IDAT_CHUNK_TYPE = new Uint8Array([0x49, 0x44, 0x41, 0x54]);
const PNG_IEND_CHUNK_TYPE = new Uint8Array([0x49, 0x45, 0x4e, 0x44]);

// 需求参数：低于阈值的小图不压缩，避免压缩收益低且可能增加耗时。
const MIN_COMPRESS_SIZE_BY_TYPE: Record<string, number> = {
  'image/jpeg': 50 * 1024,
  'image/jpg': 50 * 1024,
  [PNG_IMAGE_TYPE]: 30 * 1024,
  [WEBP_IMAGE_TYPE]: 30 * 1024,
};

// 需求参数：非 File 对象转 File 时使用的默认文件名。
const DEFAULT_FILE_NAME_BY_TYPE: Record<string, string> = {
  'image/jpeg': 'image.jpg',
  'image/jpg': 'image.jpg',
  [PNG_IMAGE_TYPE]: 'image.png',
  [GIF_IMAGE_TYPE]: 'image.gif',
  [WEBP_IMAGE_TYPE]: 'image.webp',
};

// 需求参数：JPG/WebP 压缩质量与 web worker 开关。
const IMAGE_COMPRESSION_OPTIONS = {
  initialQuality: 0.78,
  useWebWorker: true,
};

interface RgbaImage {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

interface PngMetadata {
  width: number;
  height: number;
  animated: boolean;
}

interface MessageLike {
  error(message: string): void;
}

let pngCrcTable: Uint32Array | null = null;

function getImageType(file: File | Blob) {
  return (file.type || '').toLowerCase();
}

function getFileName(file: File | Blob) {
  if (file instanceof File && file.name) {
    return file.name;
  }
  return DEFAULT_FILE_NAME_BY_TYPE[getImageType(file)] || 'image.jpg';
}

function formatFileSize(size: number) {
  return `${(size / 1024).toFixed(2)}KB`;
}

function logCompressionResult(file: File | Blob, uploadFile: File | Blob, compressed: boolean) {
  // 新增需求：记录弹窗图片压缩前后大小，便于排查上传体积变化。
  console.info('[popupImageCompression]', {
    fileName: getFileName(file),
    imageType: getImageType(file),
    beforeSize: file.size,
    beforeSizeText: formatFileSize(file.size),
    afterSize: uploadFile.size,
    afterSizeText: formatFileSize(uploadFile.size),
    savedSize: file.size - uploadFile.size,
    savedSizeText: formatFileSize(file.size - uploadFile.size),
    compressed,
  });
}

function isNoCompressCompany() {
  return NO_COMPRESS_COMPANY_IDS.includes(Number(getCompanyInfo()?.id));
}

function shouldCompressImage(file: File | Blob) {
  if (!(file instanceof Blob)) {
    return false;
  }

  const imageType = getImageType(file);
  const minCompressSize = MIN_COMPRESS_SIZE_BY_TYPE[imageType];
  return Boolean(minCompressSize && file.size >= minCompressSize && COMPRESSIBLE_IMAGE_TYPES.includes(imageType));
}

function toImageFile(file: File | Blob) {
  if (file instanceof File) {
    return file;
  }
  return new File([file], getFileName(file), {
    type: file.type || 'image/jpeg',
    lastModified: Date.now(),
  });
}

function keepOriginalFileName(file: Blob, sourceFile: File) {
  return new File([file], sourceFile.name, {
    type: sourceFile.type,
    lastModified: sourceFile.lastModified,
  });
}

function getTargetDimensions(width: number, height: number) {
  const scale = Math.min(1, MAX_IMAGE_WIDTH / width);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function createCanvas(width: number, height: number) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

function getCanvasContext(canvas: HTMLCanvasElement) {
  const context = canvas.getContext('2d');
  if (!context) {
    throw new Error('Canvas 2D context is not available');
  }
  return context;
}

function loadImage(file: Blob) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(imageUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(imageUrl);
      reject(new Error('Image decode failed'));
    };
    image.src = imageUrl;
  });
}

async function compressJpegOrWebp(sourceFile: File) {
  const image = await loadImage(sourceFile);
  const target = getTargetDimensions(image.naturalWidth, image.naturalHeight);
  return imageCompression(sourceFile, {
    ...IMAGE_COMPRESSION_OPTIONS,
    fileType: sourceFile.type,
    maxWidthOrHeight: Math.max(target.width, target.height),
  });
}

function drawImageData(image: HTMLImageElement, width: number, height: number) {
  const canvas = createCanvas(width, height);
  const context = getCanvasContext(canvas);
  context.drawImage(image, 0, 0, width, height);
  const imageData = context.getImageData(0, 0, width, height);
  canvas.width = 0;
  canvas.height = 0;
  return imageData;
}

function countUniqueColors(imageData: ImageData, stopAt: number) {
  const colors = new Set<number>();
  const pixels = imageData.data;
  for (let i = 0; i < pixels.length; i += 4) {
    const color = ((pixels[i] << 24) | (pixels[i + 1] << 16) | (pixels[i + 2] << 8) | pixels[i + 3]) >>> 0;
    colors.add(color);
    if (colors.size >= stopAt) {
      break;
    }
  }
  return colors.size;
}

function isFlatPng(image: HTMLImageElement) {
  const sample = drawImageData(image, PNG_CLASSIFICATION_SIZE, PNG_CLASSIFICATION_SIZE);
  return countUniqueColors(sample, PNG_PHOTO_UNIQUE_COLOR_THRESHOLD) < PNG_PHOTO_UNIQUE_COLOR_THRESHOLD;
}

function readPngChunkType(data: Uint8Array, offset: number) {
  return String.fromCharCode(data[offset], data[offset + 1], data[offset + 2], data[offset + 3]);
}

function readPngMetadata(sourceBuffer: ArrayBuffer): PngMetadata {
  const data = new Uint8Array(sourceBuffer);
  if (data.length < 33 || Array.from(PNG_SIGNATURE).some((value, index) => data[index] !== value)) {
    throw new Error('Invalid PNG signature');
  }

  const view = new DataView(sourceBuffer);
  let offset = PNG_SIGNATURE.length;
  let width = 0;
  let height = 0;
  let animated = false;

  while (offset + 12 <= data.length) {
    const chunkLength = view.getUint32(offset);
    const typeOffset = offset + 4;
    const chunkDataOffset = typeOffset + 4;
    const nextOffset = chunkDataOffset + chunkLength + 4;
    if (nextOffset > data.length) {
      throw new Error('Invalid PNG chunk length');
    }

    const chunkType = readPngChunkType(data, typeOffset);
    if (chunkType === 'IHDR') {
      width = view.getUint32(chunkDataOffset);
      height = view.getUint32(chunkDataOffset + 4);
    } else if (chunkType === 'acTL') {
      animated = view.getUint32(chunkDataOffset) > 1;
    } else if (chunkType === 'IDAT') {
      break;
    }
    offset = nextOffset;
  }

  if (width <= 0 || height <= 0) {
    throw new Error('Missing PNG metadata');
  }

  return {
    width,
    height,
    animated,
  };
}

async function compressFlatPng(imageData: ImageData) {
  return UPNG.encode([imageData.data.buffer as ArrayBuffer], imageData.width, imageData.height, PNG_MAX_COLORS);
}

function getPngCrcTable() {
  if (!pngCrcTable) {
    pngCrcTable = new Uint32Array(256);
    for (let index = 0; index < pngCrcTable.length; index += 1) {
      let crc = index;
      for (let bit = 0; bit < 8; bit += 1) {
        crc = (crc & 1) !== 0 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1;
      }
      pngCrcTable[index] = crc >>> 0;
    }
  }
  return pngCrcTable;
}

function getPngChunkCrc(type: Uint8Array, data: Uint8Array) {
  const table = getPngCrcTable();
  let crc = 0xffffffff;
  for (let index = 0; index < type.length; index += 1) {
    crc = table[(crc ^ type[index]) & 0xff] ^ (crc >>> 8);
  }
  for (let index = 0; index < data.length; index += 1) {
    crc = table[(crc ^ data[index]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function createPngChunk(type: Uint8Array, data: Uint8Array) {
  const chunk = new Uint8Array(data.length + 12);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, data.length);
  chunk.set(type, 4);
  chunk.set(data, 8);
  view.setUint32(data.length + 8, getPngChunkCrc(type, data));
  return chunk;
}

function hasTransparentPixel(pixels: Uint8ClampedArray) {
  for (let index = 3; index < pixels.length; index += 4) {
    if (pixels[index] !== 255) {
      return true;
    }
  }
  return false;
}

function createSubFilteredPixels(imageData: ImageData, preserveAlpha: boolean) {
  const source = imageData.data;
  const channels = preserveAlpha ? 4 : 3;
  const rowLength = imageData.width * channels;
  const filtered = new Uint8Array((rowLength + 1) * imageData.height);
  let sourceOffset = 0;
  let targetOffset = 0;
  for (let row = 0; row < imageData.height; row += 1) {
    filtered[targetOffset++] = 1;
    const previousPixel = [0, 0, 0, 0];
    for (let column = 0; column < imageData.width; column += 1) {
      for (let channel = 0; channel < channels; channel += 1) {
        const value = source[sourceOffset + channel];
        filtered[targetOffset++] = value - previousPixel[channel];
        previousPixel[channel] = value;
      }
      sourceOffset += 4;
    }
  }
  return filtered;
}

function encodeFastPng(imageData: ImageData) {
  const preserveAlpha = hasTransparentPixel(imageData.data);
  const header = new Uint8Array(13);
  const headerView = new DataView(header.buffer);
  headerView.setUint32(0, imageData.width);
  headerView.setUint32(4, imageData.height);
  header[8] = 8;
  header[9] = preserveAlpha ? 6 : 2;

  const compressedPixels = deflate(createSubFilteredPixels(imageData, preserveAlpha), { level: 1 });
  const headerChunk = createPngChunk(PNG_IHDR_CHUNK_TYPE, header);
  const imageChunk = createPngChunk(PNG_IDAT_CHUNK_TYPE, compressedPixels);
  const endChunk = createPngChunk(PNG_IEND_CHUNK_TYPE, new Uint8Array());
  const png = new Uint8Array(PNG_SIGNATURE.length + headerChunk.length + imageChunk.length + endChunk.length);
  let offset = 0;
  [PNG_SIGNATURE, headerChunk, imageChunk, endChunk].forEach((part) => {
    png.set(part, offset);
    offset += part.length;
  });
  return new Blob([png], { type: PNG_IMAGE_TYPE });
}

async function encodePhotoPng(image: HTMLImageElement, width: number, height: number) {
  const canvas = createCanvas(width, height);
  const context = getCanvasContext(canvas);
  context.drawImage(image, 0, 0, width, height);
  try {
    return encodeFastPng(context.getImageData(0, 0, width, height));
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

async function compressPng(sourceFile: File) {
  const sourceBuffer = await sourceFile.arrayBuffer();
  const metadata = readPngMetadata(sourceBuffer);
  if (metadata.animated) {
    return sourceFile;
  }

  const image = await loadImage(sourceFile);
  const flatPng = isFlatPng(image);
  const target = getTargetDimensions(metadata.width, metadata.height);
  if (!flatPng) {
    return encodePhotoPng(image, target.width, target.height);
  }

  const imageData = drawImageData(image, target.width, target.height);
  const compressedBuffer = await compressFlatPng(imageData);

  return new Blob([compressedBuffer], { type: PNG_IMAGE_TYPE });
}

async function compressByImageType(sourceFile: File) {
  const imageType = getImageType(sourceFile);
  if (JPEG_IMAGE_TYPES.includes(imageType) || imageType === WEBP_IMAGE_TYPE) {
    return compressJpegOrWebp(sourceFile);
  }
  if (imageType === PNG_IMAGE_TYPE) {
    return compressPng(sourceFile);
  }
  return sourceFile;
}

/**
 * 弹窗图片上传压缩。
 * @param file 原始上传文件或 Blob。
 * @returns 压缩收益更小时返回压缩后的 File，否则返回原始文件。
 */
export async function compressPopupUploadImage<T extends File | Blob>(file: T): Promise<T | File> {
  if (isNoCompressCompany()) {
    return file;
  }

  if (!shouldCompressImage(file)) {
    logCompressionResult(file, file, false);
    return file;
  }

  try {
    const sourceFile = toImageFile(file);
    const compressedFile = await compressByImageType(sourceFile);
    const uploadFile = compressedFile.size < file.size ? keepOriginalFileName(compressedFile, sourceFile) : file;
    logCompressionResult(file, uploadFile, uploadFile !== file);
    return uploadFile;
  } catch (error) {
    console.warn('popup image compression failed, fallback to original file', error);
    logCompressionResult(file, file, false);
    return file;
  }
}

/**
 * 弹窗图片上传前处理。
 * @param file Element Upload 传入的原始文件。
 * @param message 页面消息提示对象，用于输出格式或大小校验错误。
 * @returns false 表示阻止上传，File/Blob 表示继续上传该文件。
 */
export async function popupImageBeforeUpload(file: File, message: MessageLike): Promise<File | Blob | boolean> {
  const imageType = getImageType(file);
  if (!ALLOWED_IMAGE_TYPES.includes(imageType)) {
    message.error('上传文件只能是图片格式!');
    // 新增需求：Element Upload 的异步 before-upload 需要 reject 才能真正阻断上传。
    return Promise.reject(false);
  }

  // 新增需求：先压缩，再按上传上限校验，避免可压缩大图被提前拦截。
  const uploadFile = await compressPopupUploadImage(file);
  if (uploadFile.size > MAX_UPLOAD_SIZE) {
    message.error('上传的图片大小不超过500kb');
    // 新增需求：压缩后仍超过限制时必须阻断原文件继续上传。
    return Promise.reject(false);
  }
  return uploadFile;
}

/**
 * 弹窗图片上传前处理：立刻锁住 picture-card 的「+」入口，再压缩并做托管授权。
 * 压缩/校验/授权失败时解锁，避免上传途中多出一个上传入口。
 */
export async function preparePopupImageUpload(
  file: File,
  message: MessageLike,
  lock: () => void,
  unlock: () => void,
): Promise<File | Blob | boolean> {
  lock();
  try {
    const uploadFile = await popupImageBeforeUpload(file, message);
    if (!uploadFile) {
      unlock();
      return false;
    }
    await ensureFileScopeAuthorized();
    return uploadFile;
  } catch (e) {
    unlock();
    throw e;
  }
}
